import React, { useState } from "react";
import Layout from "./Layout";
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { createMergeReport } from "../services/API";
import alertify from "alertifyjs";
import "alertifyjs/build/css/alertify.css";
import "alertifyjs/build/css/themes/default.css";
import { useNavigate } from "react-router-dom";

function MergeReport() {
    const [reportName, setReportName] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [reportType, setReportType] = useState("");
    const [productType, setProductType] = useState("");
    const [dataSource, setDataSource] = useState("Database");
    const navigate = useNavigate();

    const yesterday = dayjs().subtract(1, "day");

    const handleGenerateReport = async () => {
        if (!reportName || !startDate || !endDate || !reportType || (reportType === "N-gram" && !productType) || !dataSource) {
            alertify.error("Please fill in all fields.", 5);
            return;
        }

        if (dayjs(startDate).isAfter(yesterday, "day") || dayjs(endDate).isAfter(yesterday, "day")) {
            alertify.error("Dates cannot be later than yesterday.", 5);
            return;
        }

        if (dayjs(startDate).isAfter(endDate)) {
            alertify.error("Start Date cannot be later than End Date.", 5);
            return;
        }

        const executeReportGeneration = async () => {
            const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
            const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

            const datesInRange = [];
            let currentDate = dayjs(startDate);
            while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
                datesInRange.push(currentDate.format("YYYY-MM-DD"));
                currentDate = currentDate.add(1, "day");
            }

            const reportData = {
                name: reportName,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                type: productType,
                allDates: datesInRange,
                dataSource: dataSource
            };

            console.log("Generar Reporte con:", reportData);
            const accountID = localStorage.getItem("selectedAccountValue");

            try {
                await createMergeReport(reportData, accountID);
                alertify.notify("Report created successfully. Processing will continue in the background.", "success", 8);
                alertify.warning("Redirecting...");

                setTimeout(() => {
                    navigate("/reportesCSV");
                }, 2000);

                setReportName("");
                setStartDate(null);
                setEndDate(null);
                setReportType("");
                setProductType("");
                setDataSource("Database");

            } catch (error) {
                console.error("Error creating merge report:", error);
                alertify.error("Error creating the report. Please try again.", 5);
            }
        };

        if (dataSource === "AmazonAPI") {
            alertify.confirm(
                "Confirmation Required",
                "You have selected Amazon API as the data source. This process may take 30 minutes or more. Do you want to continue?",
                function () {
                    executeReportGeneration();
                },
                function () {
                    alertify.error("Operation cancelled");
                }
            );
        } else {
            executeReportGeneration();
        }
    };

    const handleCancel = () => {
        navigate("/reportesCSV");
    };

    return (
        <Layout>
            <div>
                <Box sx={{ p: 4, bgcolor: "background.paper", borderRadius: 2, boxShadow: 3, maxWidth: 900, mx: "auto", mt: 4 }}>
                    <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3 }}>
                        Configure New Report
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Report Name"
                                variant="outlined"
                                value={reportName}
                                onChange={(e) => setReportName(e.target.value)}
                            />
                        </Grid>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    maxDate={yesterday}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    maxDate={yesterday}
                                />
                            </Grid>
                        </LocalizationProvider>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ width: 200 }}>
                                <InputLabel id="report-type-label">Report Type</InputLabel>
                                <Select
                                    labelId="report-type-label"
                                    id="report-type-select"
                                    value={reportType}
                                    label="Report Type"
                                    onChange={(e) => {
                                        setReportType(e.target.value);
                                        if (e.target.value !== "N-gram") {
                                            setProductType("");
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select type</em>
                                    </MenuItem>
                                    <MenuItem value="N-gram">N-gram</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {reportType === "N-gram" && (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth sx={{ width: 200 }}>
                                    <InputLabel id="product-type-label">Product Type</InputLabel>
                                    <Select
                                        labelId="product-type-label"
                                        id="product-type-select"
                                        value={productType}
                                        label="Product Type"
                                        onChange={(e) => setProductType(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Select product type</em>
                                        </MenuItem>
                                        <MenuItem value="Sponsored Product">Sponsored Product</MenuItem>
                                        <MenuItem value="Sponsored Brand">Sponsored Brand</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Data Source</FormLabel>
                                <RadioGroup
                                    row
                                    name="dataSource"
                                    value={dataSource}
                                    onChange={(e) => setDataSource(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="Database"
                                        control={<Radio />}
                                        label="Database"
                                    />
                                    <FormControlLabel
                                        value="AmazonAPI"
                                        control={<Radio />}
                                        label="Amazon API"
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>


                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleGenerateReport}
                                >
                                    Generate Report
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </div>
        </Layout>
    );
}

export default MergeReport;